import * as _ from 'lodash'
import {
	VNumber, A, Button, Div, Variable, ElementRenderer, Element,
} from 'alkali'

/**
 * @name Tabbed
 * @description
 * Main component, usable on its own.
 *
 * Alternatively, sub-components may be used together manually
 * to allow for alternative DOM structures when necessary.
 *
 * @param {Array.<Object>} content
 * @param {String} content.label
 * @param {Function||DOMObject} content.content
 *
 * @example
 * ```js
 * new Tabbed([{
 *	 label: 'Foo',
 *	 content: new Div() // Component or DOM object
 * }, {
 *	 // Alternatively, a function can be specified for lazyily
 *	 // instantiating content when tab becomes active.
 *	 content: () => new Div(),
 *	 label: 'Bar',
 * }])
 * ```
 */

export class Tabbed extends Div('.tabbed') {
	created(props) {
		const { content } = props
		props.content = []

		this.state = props.tabbedState || new TabbedState(props.activeIndex)

		const activeIndex = this.state.property('activeIndex')

		this.append(
			new TabbedNavigation({ activeIndex, content, leading: props.leading, trailing: props.trailing }),
			new TabbedContent({ activeIndex, content }),
		)
	}
}

/**
 * @name TabbedState
 * @description
 * Exported so we can use TabbedContent and TabbedNavigation
 * on their own without the main Tabbed component.
 */

export class TabbedState extends Variable.with({
	activeIndex: VNumber
}) {
	constructor(activeIndex = 0) {
		const initialState = {
			activeIndex
		}

		super(initialState)
	}
}

/**
 * @name TabbedContent
 * @description
 * Content container for the tabs.
 *
 * @param {Variable.<Integer>} activeIndex
 * @param {Array.<Function|DOMObject>} content
 */

export class TabbedContent extends Div.with('.tabbed-content') {
	created(props) {
		const { content, activeIndex } = props
		props.content = []

		this.append(content.map((tab, index) => new TabbedPane({
			content: tab.content,
			active: activeIndex.to((activeIndex) => index === activeIndex),
			index,
		})))
	}
}

/**
 * @name TabbedPane
 * @description
 * Container for an individual tab's content
 *
 * @param {Variable.<Boolean>} active
 * @param {Function|DOMObject} content
 */

export class TabbedPane extends Div.with('.tabbed-pane') {
	created(props) {
		const { content, active } = props

		props.content = []
		props.classes = props.classes || {}

		Object.assign(props.classes, {
			active,
			inactive: active.to((active) => !active),
		})

		// Used for lazy tab content initialization
		this.initialized = false

		// Content lazily instantiated when tab is activated
		if (_.isFunction(content)) {
			new ElementRenderer({
				element: this,
				variable: active,
				renderUpdate: (isActive) => {
					if (isActive && !this.initialized) {
						this.append(content(this, props.index))
						this.initialized = true
					}
					this.focus()
				},
			})
		} else {
			this.append(content)
		}
	}
}

/**
 * @name TabbedNavigation
 * @example
 * ```js
 * new TabbedNavigation({
 *	 activeIndex: reactive(0)
 * }, [{ label: 'Foobar'}])
 * ```
 */

export class TabbedNavigation extends Element.with('.tabbed-navigation') {
	static tagName = 'tabbed-nav'

	created(props) {
		const { content, activeIndex, leading, trailing } = props
		props.content = []

		leading && this.append(leading)
		this.append(
			content.map((tab, index) => Button(tab.label, _.merge({
				display: tab.display || true,
				classes: {
					active: activeIndex.to(activeIndex => activeIndex === index),
					inactive: activeIndex.to(activeIndex => activeIndex != index)
				},
				onclick: (event) => activeIndex.put(index)
			}, tab.config || {})))
		)
		trailing && this.append(trailing)
	}

	// Experimenting with keyboard navigation
	// onkeydown(e) {
	//	 // if (!e.shiftKey) return
	//	 const i = this.activeIndex.valueOf()
	//	 debugger
	//	 if (e.key === 'ArrowRight')
	//		 this.activeIndex.put(i + 1)
	//	 else if (e.key === 'ArrowLeft')
	//		 this.activeIndex.put(i - 1)
	// }
}

export default Tabbed
